import React, { useState, useEffect, useMemo } from "react";
import { ReactComponent as Van } from "../../assets/descriptor/van.svg";
import { ReactComponent as Unit } from "../../assets/descriptor/unit.svg";
import { ReactComponent as Box } from "../../assets/descriptor/box.svg";
import Descriptor from "../../features/rapidstor/entities/Descriptor";
import classes from "./Descriptor.module.scss";
import { Col, Row } from "react-bootstrap";
import PrimaryButton from "../Elements/PrimaryButton";
import DescriptorFact from "./DescriptorFact";
import DescriptorPricingBox from "./DescriptorPricingBox";
import { ReactComponent as Headset } from "../../assets/icon-headset.svg";
import { useLoaderData, useNavigate } from "react-router-dom";
import Location from "../../features/rapidstor/entities/Location";
import ReactSimplyCarousel from "./PhotoCarousel";
import { selectUnitData, setUnitData } from "../../store/bookingSlice";
import store from "../../store";
import { useSelector } from "react-redux";
import { locationCompanyProfile } from "../../features/helpers";

interface Props extends React.HTMLAttributes<HTMLElement> {
  descriptor: Descriptor;
  onSelectSizeClicked?: () => void;
  locationPhone?: string;
  buttonText?: string;
  locationSlug?: string;
  showDetails?: boolean;
  showFacts?: boolean;
}

export interface FactType {
  icon: JSX.Element;
  text: string;
}

function Descriptor({
  onSelectSizeClicked,
  // locationSlug,
  descriptor,
  buttonText = "See Prices",
  showDetails,
  showFacts,
}: Props) {
  const location = useLoaderData() as Location;
  const navigate = useNavigate();
  const companyProfile = locationCompanyProfile();

  const [facts, setFacts] = useState<FactType[]>([]);
  const [available, setAvailable] = useState<boolean>(false);
  const [carouselPhotos, setCarouselPhotos] = useState<string[]>([]);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const unitData = useSelector(selectUnitData);

  useEffect(() => {
    const sortedFacts: FactType[] = [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    setAvailable(descriptor.unitTypes.find((t) => t.hasComputedAvailable) !== undefined);

    if (descriptor.slides.length != 0) {
      setCarouselPhotos(descriptor.slides);
    } else {
      setCarouselPhotos([descriptor.picture]);
    }

    if (descriptor.descriptions.length > 0) {
      sortedFacts.push({
        icon: <Box />,
        text: descriptor.descriptions[0],
      });
    }
    if (descriptor.descriptions.length > 1) {
      sortedFacts.push({
        icon: <Van />,
        text: descriptor.descriptions[1],
      });
    }
    if (descriptor.descriptions.length > 2) {
      sortedFacts.push({
        icon: <Unit />,
        text: descriptor.descriptions[2],
      });
    }

    setFacts(sortedFacts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor.area, descriptor.unitTypes]);

  const buttonClick = () => {
    if ((!showDetails || available) && onSelectSizeClicked) {
      onSelectSizeClicked();
    }
  };

  const sqm = useMemo(() => {
    if (typeof descriptor.area === "number") {
      return (descriptor.area * 0.092903).toLocaleString("en-GB", {
        maximumFractionDigits: 1,
      });
    }
    return (parseFloat(descriptor.area) * 0.092903).toLocaleString("en-GB", {
      maximumFractionDigits: 1,
    });
  }, [descriptor.area]);

  return (
    <div className={classes.descriptor}>
      <Row>
        <Col xs={6}>
          {/* <img src={descriptor.picture} /> */}
          <ReactSimplyCarousel
            activeSlideIndex={carouselIndex}
            onRequestChange={setCarouselIndex}
            containerProps={{
              style: {
                width: "100%",
                userSelect: "none",
              },
            }}
            swipeTreshold={60}
            forwardBtnProps={{
              disabled: carouselIndex === carouselPhotos.length - 1,
            }}
            backwardBtnProps={{
              disabled: carouselIndex === 0,
            }}
            itemsToShow={1}
            itemsToScroll={1}
            hideNavIfAllVisible={false}
            speed={300}
            infinite={false}
          >
            {carouselPhotos.map((img, index) => (
              <div key={`${index}_slide_idx`} className={classes.slide}>
                <img src={img} />
              </div>
            ))}
          </ReactSimplyCarousel>
        </Col>
        <Col xs={6}>
          <div className={classes.descriptor_pill_container}>
            <div className={classes.descriptor_stats}>
              <div>
                <span className={classes.descriptor_area}>{descriptor.area}</span>
              </div>
              <div className={classes.descriptor_small}>
                <span>sqft</span>
                <span>({sqm}m²)</span>
              </div>
            </div>
          </div>
          <p className={`${classes.descriptor_description} ${classes.desktop}`}>
            {descriptor.description ? descriptor.description : ""}
          </p>
          {showFacts ? (
            <Row className={classes.desktop}>
              <Col>
                {facts.map((fact, idx) => {
                  return (
                    <DescriptorFact key={`descriptor_fact_idx_${idx}`} fact={fact} />
                  );
                })}
              </Col>
            </Row>
          ) : (
            ""
          )}
          {showDetails ? (
            <Row className={classes.desktop}>
              <Col>
                <DescriptorPricingBox descriptor={descriptor} />
              </Col>
            </Row>
          ) : (
            ""
          )}
        </Col>
      </Row>
      <p className={`${classes.descriptor_description} ${classes.mobile}`}>
        {descriptor.description ? descriptor.description : ""}
      </p>
      {showFacts ? (
        <Row className={classes.mobile}>
          <Col>
            {facts.map((fact, idx) => {
              return <DescriptorFact key={`descriptor_fact_idx_${idx}`} fact={fact} />;
            })}
          </Col>
        </Row>
      ) : (
        ""
      )}
      {showDetails ? (
        <Row className={classes.mobile}>
          <Col>
            <DescriptorPricingBox descriptor={descriptor} />
          </Col>
        </Row>
      ) : (
        ""
      )}
      <Row style={{ justifyContent: "center" }}>
        {showDetails && !available ? (
          <>
            <Col
              style={{ marginTop: "20px", textAlign: "center" }}
              xs={12}
              sm={12}
              md={4}
            >
              <PrimaryButton
                onClick={() => {
                  if (unitData) {
                    store.dispatch(
                      setUnitData({
                        ...unitData,
                        unitType: descriptor.unitTypes[0],
                        selectedDescriptor: descriptor,
                      })
                    );
                  }
                  navigate(`/${location.slug}/callback`);
                }}
                responsive
              >
                CALL ME
              </PrimaryButton>
            </Col>
            <Col
              style={{ marginTop: "20px", textAlign: "center" }}
              xs={12}
              sm={12}
              md={4}
            >
              <PrimaryButton
                callNow={showDetails && !available ? true : false}
                responsive
              >
                CALL US NOW
              </PrimaryButton>
            </Col>
          </>
        ) : (
          <>
            <Col style={{ marginTop: "20px", textAlign: "center" }}>
              <PrimaryButton
                onClick={buttonClick}
                callNow={showDetails && !available ? true : false}
                responsive
              >
                {!available && showDetails ? "CALL US NOW" : buttonText}
              </PrimaryButton>
            </Col>
          </>
        )}
        <span className={`${classes.descriptor_not_sure}`}>
          <Headset /> Not sure? Call us FREE on{" "}
          <a href={`tel:${companyProfile["phone-number"]}`}>
            {companyProfile["phone-number"]}
          </a>
        </span>
      </Row>
    </div>
  );
}

export default Descriptor;
